import { property, state } from 'lit/decorators.js'
import { html, nothing } from 'lit'

import LfdsIcon from '#components/icon/icon/lfds-icon.ts'
import { LfuiElement } from '#utils/element'
import styles from './lfds-radio-group.css.ts'

type RadioEl = HTMLElement & {
	name?: string
	value?: string
	checked?: boolean
	disabled?: boolean
}

/**
 * @name LfdsRadioGroup
 *
 * @status beta
 * @tag lfds-radio-group
 *
 * @description
 * Groups `lfds-radio` (and `lfds-radio-card`) to enable arrow-key navigation
 * and enforce a shared `name`. Renders a `role="radiogroup"` container with an
 * optional visible label, help text, and validation message.
 *
 * Set `name` on the group and it propagates to all child radios. If a child
 * already has a different name, it is overridden and a console warning is emitted.
 *
 * @slot label - The group label text. Can also be set via the `label` attribute.
 * @slot - Default slot for `lfds-radio` / `lfds-radio-card` elements.
 */
export default class LfdsRadioGroup extends LfuiElement {
	static styles = [LfuiElement.styles, styles]
	static events = ['change']
	static formAssociated = true

	// Group label / help / validation
	@property() label: string
	@property({ attribute: 'help-text' }) helpText: string
	@property({ attribute: 'validation-message' }) validationMessage: string

	// ARIA is applied on the host element
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string

	/** The name to assign to all child radios in the group. */
	@property() name: string

	/** Current selected value (reflected from the checked child). */
	@property() value: string

	/** Mark the group as required — a radio must be selected before form submission. */
	@property({ type: Boolean }) required: boolean

	/** Set to true when the group is in an invalid state (mirrors lfds-input pattern). */
	@property({ type: Boolean }) invalid: boolean

	@state() private _radios: RadioEl[] = []
	#mo?: MutationObserver
	#internals: ElementInternals

	/** The value of the initially-checked radio, used to restore state on form reset. */
	#defaultValue: string | undefined = undefined

	// Stable IDs for aria-labelledby / aria-describedby
	#labelId = `lfds-rg-label-${Math.random().toString(36).slice(2, 8)}`
	#helpId = `lfds-rg-helpvis-${Math.random().toString(36).slice(2, 8)}`
	#lightLabelId = `lfds-rg-ll-${Math.random().toString(36).slice(2, 8)}`
	#lightHelpId = `lfds-rg-help-${Math.random().toString(36).slice(2, 8)}`
	#validationId = `lfds-rg-validation-${Math.random().toString(36).slice(2, 8)}`

	constructor() {
		super()
		this.#internals = this.attachInternals()
		LfdsIcon.preload('alert', 24)
	}

	connectedCallback() {
		super.connectedCallback()
		this.addEventListener('keydown', this.#onKeyDown as EventListener)
		this.addEventListener('change', this.#onChildChange as EventListener)
		this.#setupObserver()

		// Mirror lfds-input: when the browser fires the invalid event (e.g. on
		// form.reportValidity()), show the native message and set the invalid flag.
		this.addEventListener('invalid', () => {
			this.invalid = true
			if (!this.validationMessage) {
				this.validationMessage = this.#internals.validationMessage
			}
		})
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('keydown', this.#onKeyDown as EventListener)
		this.removeEventListener('change', this.#onChildChange as EventListener)
		this.#teardownObserver()
	}

	/**
	 * Called automatically by the browser when the associated form resets.
	 * More reliable than manually listening to the form's reset event.
	 */
	formResetCallback() {
		this.#onFormReset()
	}

	firstUpdated() {
		this.#collectRadios()
		this.#applyGroupName()
		this.#applyGroupLabel()
		this.#applyGroupDescription()
		this.#syncRovingTabIndex()
		this.#syncFormValue()
		this.#syncValidity()
		// Cache the initial value so formResetCallback can restore it,
		// matching native <input type="radio"> reset behaviour.
		this.#defaultValue = this.value

		// Re-apply label when the label slot changes
		const labelSlot = this.shadowRoot?.querySelector(
			'slot[name="label"]'
		) as HTMLSlotElement
		labelSlot?.addEventListener('slotchange', () => this.#applyGroupLabel())
	}

	updated(changed: Map<string, unknown>) {
		super.updated(changed)
		if (changed.has('name')) {
			this.#applyGroupName()
			this.#syncRovingTabIndex()
		}
		if (changed.has('label')) {
			this.#applyGroupLabel()
		}
		if (changed.has('helpText')) {
			this.#applyGroupDescription()
		}
		// Re-validate when required changes (e.g. dynamic forms)
		if (changed.has('required') || changed.has('value')) {
			this.#syncValidity()
		}
	}

	#slotChanged = () => {
		this.#collectRadios()
		this.#applyGroupName()
		this.#applyGroupLabel()
		this.#applyGroupDescription()
		this.#syncRovingTabIndex()
	}

	#collectRadios() {
		// Use querySelectorAll on the group's light DOM rather than
		// slot.assignedElements() — slot assignment timing is not guaranteed
		// to be complete by the time firstUpdated fires.
		const isRadio = (el: Element): el is RadioEl => {
			const tag = el.tagName.toLowerCase()
			return tag === 'lfds-radio' || tag === 'lfds-radio-card'
		}

		const collected: RadioEl[] = []
		const walk = (root: Element) => {
			if (isRadio(root)) collected.push(root)
			for (const child of Array.from(root.children)) walk(child)
		}
		for (const child of Array.from(this.children)) walk(child)

		// Only update state if list actually changed to avoid update loops
		const sameLength = collected.length === this._radios.length
		const sameOrder =
			sameLength && collected.every((el, i) => el === this._radios[i])
		if (!sameOrder) {
			this._radios = collected
		}
	}

	#setupObserver() {
		if (this.#mo) return
		this.#mo = new MutationObserver(() => {
			this.#collectRadios()
			this.#applyGroupName()
			this.#syncRovingTabIndex()
		})
		// Observe light DOM children (not shadow)
		this.#mo.observe(this, { childList: true, subtree: true })
	}

	#teardownObserver() {
		this.#mo?.disconnect()
		this.#mo = undefined
	}

	#applyGroupName() {
		if (!this.name) return
		for (const radio of this._radios) {
			if (
				radio.getAttribute('name') &&
				radio.getAttribute('name') !== this.name
			) {
				// Warn once per child per mismatch
				console.warn('[lfds-radio-group] Overriding child radio name', {
					previous: radio.getAttribute('name'),
					next: this.name,
					radio
				})
			}
			radio.setAttribute('name', this.name)
			// Also set property to ensure Lit updates immediately
			;(radio as any).name = this.name
		}
	}

	/**
	 * Stamp the group label ID onto each child radio's sr-labelledby so
	 * screen readers announce the group label before each radio's own label.
	 *
	 * The ID is placed on a light-DOM element (the slotted label or a generated
	 * span wrapping the `label` attribute text) so the radio's ariaElements
	 * directive can resolve it by walking up the DOM tree.
	 */
	#applyGroupLabel() {
		// Resolve the light-DOM label element
		let labelEl = this.querySelector('[slot="label"]') as HTMLElement | null

		// If using the `label` attribute (no slotted element), create a hidden
		// light-DOM anchor so radios can reference it via ID
		if (!labelEl && this.label) {
			labelEl = this.querySelector(
				`#${this.#lightLabelId}`
			) as HTMLElement | null
			if (!labelEl) {
				labelEl = document.createElement('span')
				labelEl.id = this.#lightLabelId
				labelEl.setAttribute('hidden', '')
				labelEl.setAttribute('aria-hidden', 'true')
				labelEl.textContent = this.label
				this.prepend(labelEl)
			} else {
				labelEl.textContent = this.label
			}
		}

		if (!labelEl) return

		// Ensure the element has our stable ID
		if (!labelEl.id) {
			labelEl.id = this.#lightLabelId
		}
		const labelId = labelEl.id

		// Prepend the group label ID to each radio's sr-labelledby
		for (const radio of this._radios) {
			const existing = radio.getAttribute('sr-labelledby') || ''
			// Don't add if already present
			if (existing.split(/\s+/).includes(labelId)) continue
			radio.setAttribute(
				'sr-labelledby',
				existing ? `${labelId} ${existing}` : labelId
			)
		}
	}

	/**
	 * Stamp the group help-text ID onto each child radio's sr-describedby so
	 * screen readers announce the help text with each radio.
	 */
	#applyGroupDescription() {
		if (!this.helpText) {
			// Remove previously created anchor if help text was cleared
			const existing = this.querySelector(`#${this.#lightHelpId}`)
			if (existing) existing.remove()
			// Remove our ID from radios' sr-describedby
			for (const radio of this._radios) {
				const desc = radio.getAttribute('sr-describedby') || ''
				const updated = desc
					.split(/\s+/)
					.filter((id) => id && id !== this.#lightHelpId)
					.join(' ')
				if (updated) {
					radio.setAttribute('sr-describedby', updated)
				} else {
					radio.removeAttribute('sr-describedby')
				}
			}
			return
		}

		// Create or update the hidden light-DOM anchor
		let helpEl = this.querySelector(
			`#${this.#lightHelpId}`
		) as HTMLElement | null
		if (!helpEl) {
			helpEl = document.createElement('span')
			helpEl.id = this.#lightHelpId
			helpEl.setAttribute('hidden', '')
			helpEl.setAttribute('aria-hidden', 'true')
			this.append(helpEl)
		}
		helpEl.textContent = this.helpText

		// Append the help text ID to each radio's sr-describedby
		for (const radio of this._radios) {
			const existing = radio.getAttribute('sr-describedby') || ''
			if (existing.split(/\s+/).includes(this.#lightHelpId)) continue
			radio.setAttribute(
				'sr-describedby',
				existing ? `${existing} ${this.#lightHelpId}` : this.#lightHelpId
			)
		}
	}

	#onFormReset = () => {
		// Restore every radio to the state it had when the element first rendered,
		// matching how native <input type="radio"> responds to form.reset().
		for (const radio of this._radios) {
			;(radio as any).checked = (radio as any).value === this.#defaultValue
		}
		this.value = this.#defaultValue
		this.invalid = false
		this.validationMessage = undefined
		this.#internals.setFormValue(this.#defaultValue ?? null)
		this.#syncValidity()
		this.#syncRovingTabIndex()
	}

	#syncFormValue() {
		const checked = this._radios.find((r) => (r as any).checked)
		if (checked) {
			this.value = (checked as any).value
			this.#internals.setFormValue(this.value)
		} else {
			this.#internals.setFormValue(null)
		}
	}

	/** Sync ElementInternals validity to match current required + value state. */
	#syncValidity() {
		if (this.required && !this.value) {
			// Note: the anchor for setValidity must be inside this element's own
			// shadow DOM. Radios live in the light DOM so we cannot use them.
			// Omitting anchor still prevents form submission; reportValidity() will
			// scroll the group into view via the host element instead.
			this.#internals.setValidity({ valueMissing: true }, 'Välj ett alternativ')
		} else {
			this.#internals.setValidity({})
		}
	}

	/** Proxy for ElementInternals.checkValidity() */
	checkValidity() {
		return this.#internals.checkValidity()
	}

	/** Proxy for ElementInternals.reportValidity() */
	reportValidity() {
		return this.#internals.reportValidity()
	}

	#getEnabledRadios() {
		return this._radios.filter((r) => !(r as any).disabled)
	}

	#getActiveIndex() {
		const enabled = this.#getEnabledRadios()
		if (enabled.length === 0) return -1
		const checkedIndex = enabled.findIndex((r) => (r as any).checked)
		if (checkedIndex >= 0) return this._radios.indexOf(enabled[checkedIndex])
		// Default to first enabled
		return this._radios.indexOf(enabled[0])
	}

	#focusRadio(radio: RadioEl) {
		// Focus internal input for exactness and set selection
		const input = radio.shadowRoot?.getElementById(
			'input'
		) as HTMLInputElement | null
		if (input) input.focus()
	}

	#selectRadio(target: RadioEl, dispatch = true) {
		for (const radio of this._radios) {
			;(radio as any).checked = radio === target
		}
		this.value = (target as any).value
		this.#internals.setFormValue(this.value)
		this.invalid = false
		this.validationMessage = undefined
		this.#syncValidity()
		this.#syncRovingTabIndex()

		if (dispatch) {
			// Emit a group-level change event mirroring native shape
			this.dispatchEvent(
				new CustomEvent('change', {
					detail: { value: (target as any).value },
					bubbles: true,
					composed: true
				})
			)
		}
	}

	#syncRovingTabIndex() {
		// Ensure only one tabbable input within the group and communicate
		// set-size to assistive technology. Each radio lives in its own Shadow DOM
		// so the browser cannot infer the group size — without explicit attributes
		// screen readers announce "1 of 1" instead of e.g. "1 of 3".
		const activeIndex = this.#getActiveIndex()
		const total = this._radios.length
		for (let i = 0; i < total; i++) {
			const radio = this._radios[i]
			const input = radio.shadowRoot?.getElementById(
				'input'
			) as HTMLInputElement | null
			if (!input) continue
			input.tabIndex = i === activeIndex ? 0 : -1
			input.setAttribute('aria-setsize', String(total))
			input.setAttribute('aria-posinset', String(i + 1))
		}
	}

	#onChildChange = (event: Event) => {
		// Only react to events originating from our child radios, not from the
		// group's own dispatched change event (whose composedPath starts at the
		// group itself, not at a child radio, so the find returns undefined).
		const path = event.composedPath()
		const radio = this._radios.find((r) => path.includes(r))
		if (!radio) return

		// Mutual exclusion is handled inside each radio via LfdsRadio.groups.
		// The group only needs to track the selected value and keep tabindex in sync.
		this.value = (radio as any).value
		this.#internals.setFormValue(this.value)
		this.invalid = false
		this.validationMessage = undefined
		this.#syncValidity()
		this.#syncRovingTabIndex()
	}

	#onKeyDown = (event: KeyboardEvent) => {
		// Enter submits the associated form, matching native radio behaviour.
		// The inner <input> lives in shadow DOM and has no form owner, so the
		// browser won't do this automatically — we must handle it ourselves.
		if (event.key === 'Enter') {
			this.#internals.form?.requestSubmit()
			return
		}

		const keys = [
			'ArrowLeft',
			'ArrowRight',
			'ArrowUp',
			'ArrowDown',
			'Home',
			'End',
			' '
		] as const
		if (!keys.includes(event.key as any)) return

		const enabled = this.#getEnabledRadios()
		if (enabled.length === 0) return

		const path = event.composedPath()
		const currentChild = this._radios.find((r) => path.includes(r))
		const current =
			currentChild && !(currentChild as any).disabled
				? currentChild
				: enabled[0]

		const currentIndex = enabled.indexOf(current as RadioEl)
		let nextIndex = currentIndex

		switch (event.key) {
			case 'ArrowLeft':
			case 'ArrowUp':
				nextIndex = (currentIndex - 1 + enabled.length) % enabled.length
				break
			case 'ArrowRight':
			case 'ArrowDown':
				nextIndex = (currentIndex + 1) % enabled.length
				break
			case 'Home':
				nextIndex = 0
				break
			case 'End':
				nextIndex = enabled.length - 1
				break
			case ' ': // Space selects current
				event.preventDefault()
				if (!(current as any).checked) {
					this.#selectRadio(current as RadioEl)
				}
				this.#focusRadio(current as RadioEl)
				return
		}

		event.preventDefault()
		const next = enabled[nextIndex]
		this.#selectRadio(next)
		this.#focusRadio(next)
	}

	render() {
		return html`
			<div
				role="radiogroup"
				aria-labelledby=${this.label || this.querySelector('[slot="label"]')
					? this.#labelId
					: nothing}
				aria-describedby=${[
					this.helpText ? this.#helpId : null,
					this.validationMessage ? this.#validationId : null
				]
					.filter(Boolean)
					.join(' ') || nothing}
				aria-invalid=${this.invalid ? 'true' : nothing}
				aria-required=${this.required ? 'true' : nothing}
			>
				${this.label || this.querySelector('[slot="label"]')
					? html`<span id=${this.#labelId} class="label">
							<slot name="label">${this.label}</slot>
						</span>`
					: nothing}
				${this.helpText
					? html`<span id=${this.#helpId} class="help">${this.helpText}</span>`
					: nothing}

				<div class="radios">
					<slot @slotchange=${this.#slotChanged}></slot>
				</div>

				${this.validationMessage
					? html`
							<div
								id=${this.#validationId}
								class="validation"
								role="alert"
								aria-atomic="true"
							>
								<span class="icon">
									<lfds-icon
										icon-id="alert"
										size="24"
										color="currentcolor"
									></lfds-icon>
								</span>
								<span>${this.validationMessage}</span>
							</div>
						`
					: nothing}
			</div>
		`
	}
}
